/**
 * THE CALENDAR FOR A GUEST.
 *
 * A Guest is a brand's client who happens to be a user, so they get the team's
 * calendar UI — read-only — rather than a separate one. Two things differ:
 *
 *  1. STATUS. The team's stage vocabulary ("Chosen internally", "Client
 *     approved", "Creative brief") is the team's workflow, not the client's.
 *     A guest sees one of three words, or nothing:
 *       - "Needs approval": a concept, or a sent creative brief, is waiting on
 *         the client's answer;
 *       - "With team": the client has answered and the team is working;
 *       - "Live": the post is published.
 *     A post the team has not shared with the client yet never reaches a guest:
 *     the server leaves it out of every read (backend clientVisibility.ts), and
 *     on a post that has, it drops every idea but the one the client was shown.
 *     The client has no Publishing tab, and no Kit until a file is uploaded.
 *
 *  2. ANSWERS. Where the team would act, a guest answers as the client does —
 *     approve or send back a concept or a brief, and comment — through the
 *     guest routes (the client portal's own use cases, attributed to the
 *     guest's client row). The hooks below write the outcome into the TEAM
 *     calendar's caches first (optimistic), then reconcile.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import {
  latestReviewDecision,
  openDeclineFor,
  type CalendarPlanSlot,
  type ConceptVersionsResponse,
} from "@/types/contentCalendar.type";
import type { BriefComment, BriefRailInfo, ProductionBrief } from "@/types/productionBrief.type";
import { publicationIntegrityOf } from "./publicationIntegrity";
import { cancelPlanFetches, patchSlot, planWriteKey, putSlot, readSlot, reconcilePlan, settlePlanWrite } from "./api/calendarCache";
import { conceptVersionsKey } from "./api/useConceptVersions";
import { planBriefsKey, slotBriefKey } from "./api/useProductionBrief";

export type GuestStatus = "needs_approval" | "with_team" | "live";

export const GUEST_STATUS_LABEL: Record<GuestStatus, string> = {
  needs_approval: "Needs approval",
  with_team: "With team",
  live: "Live",
};

/** Has this post ever reached the client? Before that, a guest sees no status. */
function reachedClient(slot: CalendarPlanSlot): boolean {
  return (
    slot.status === "IN_CLIENT_REVIEW" ||
    slot.status === "CLIENT_APPROVED" ||
    !!latestReviewDecision(slot) ||
    !!openDeclineFor(slot)
  );
}

/** The one status a guest sees for a post, or null for none. */
export function guestStatusOf(
  slot: CalendarPlanSlot,
  brief?: Pick<BriefRailInfo, "status"> | null,
): GuestStatus | null {
  const publication = publicationIntegrityOf(slot);
  if (publication.state === "live" || publication.state === "partially_live") return "live";
  if (slot.status === "IN_CLIENT_REVIEW") return "needs_approval";
  if (slot.status === "CLIENT_APPROVED" && brief?.status === "IN_REVIEW") return "needs_approval";
  return reachedClient(slot) ? "with_team" : null;
}

export function guestStatusLabel(
  slot: CalendarPlanSlot,
  brief?: Pick<BriefRailInfo, "status"> | null,
): string | null {
  const status = guestStatusOf(slot, brief);
  return status ? GUEST_STATUS_LABEL[status] : null;
}

/** The guest routes for one month (see backend GuestCalendarController). */
function monthBase(brandId: string, planId: string) {
  return `/guest-calendar/${brandId}/months/${planId}`;
}

type Decision = "APPROVED" | "DECLINED";

function reportError(title: string) {
  return (err: unknown) =>
    queue.add({
      title,
      description: getAiRequestErrorMessage(err, "Please try again."),
      variant: "error",
    });
}

/**
 * A guest's answers inside the team calendar. Optimistic on what is certain
 * the moment the answer is sent — this post / brief now has the client's
 * answer — and server-decided for the rest (a round closing, the next stage),
 * which the invalidations reconcile.
 */
export function useGuestReview(brandId: string, planId: string, slotId: string) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const myName = me?.name?.trim() || me?.email || "You";
  const base = monthBase(brandId, planId);

  const resync = () => {
    reconcilePlan(qc, planId);
    void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) });
    void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
    void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
  };

  const decideConcept = useMutation({
    mutationKey: [...planWriteKey(planId), "guestDecision"],
    mutationFn: async (vars: { decision: Decision; reason?: string; conceptVersionId?: string }) =>
      (
        await axiosInstance.post(`${base}/slots/${slotId}/decision`, {
          decision: vars.decision,
          reason: vars.reason ?? null,
          conceptVersionId: vars.conceptVersionId,
        })
      ).data,
    onMutate: async (vars) => {
      await Promise.all([
        cancelPlanFetches(qc, planId),
        qc.cancelQueries({ queryKey: conceptVersionsKey(slotId) }),
        qc.cancelQueries({ queryKey: slotBriefKey(slotId) }),
      ]);
      const previousVersions = qc.getQueryData<ConceptVersionsResponse>(conceptVersionsKey(slotId));
      const previousSlot = readSlot(qc, planId, slotId);
      const previousBrief = qc.getQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(slotBriefKey(slotId));
      // One answer per post: a brief that went out with the idea is decided by
      // the same click, and a decline keeps the idea picked for version 2.
      const withBrief = previousBrief?.brief?.status === "IN_REVIEW";
      if (withBrief) {
        qc.setQueryData(slotBriefKey(slotId), {
          ...previousBrief,
          brief: { ...previousBrief.brief!, status: vars.decision === "APPROVED" ? "ACCEPTED" : "CHANGES_REQUESTED" },
        });
      }
      const decidedAt = new Date().toISOString();
      qc.setQueryData<ConceptVersionsResponse>(conceptVersionsKey(slotId), (data) =>
        data
          ? {
              ...data,
              versions: data.versions.map((v) =>
                v.id === vars.conceptVersionId
                  ? { ...v, readOnly: true, decision: { decision: vars.decision, reason: vars.reason ?? null, clientName: myName, decidedAt } }
                  : v,
              ),
            }
          : data,
      );
      // An approval moves the post on; a decline hands it back to the team.
      patchSlot(qc, planId, slotId, (slot) => ({
        ...slot,
        status: vars.decision === "APPROVED" ? "CLIENT_APPROVED" : withBrief ? "SIGNED_OFF" : "PLANNED",
      }));
      return { previousVersions, previousSlot, previousBrief: withBrief ? previousBrief : undefined };
    },
    onError: (err, _vars, context) => {
      if (context?.previousVersions) qc.setQueryData(conceptVersionsKey(slotId), context.previousVersions);
      if (context?.previousSlot) putSlot(qc, planId, context.previousSlot);
      if (context?.previousBrief) qc.setQueryData(slotBriefKey(slotId), context.previousBrief);
      reportError("Your answer was not sent")(err);
    },
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) });
      void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
    },
  });

  const commentOnConcept = useMutation({
    mutationFn: async (vars: { versionId: string; body: string }) =>
      (await axiosInstance.post(`${base}/concept-versions/${vars.versionId}/comments`, { body: vars.body })).data,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: conceptVersionsKey(slotId) });
      const tempId = `optimistic-${Date.now()}`;
      qc.setQueryData<ConceptVersionsResponse>(conceptVersionsKey(slotId), (data) =>
        data
          ? {
              ...data,
              versions: data.versions.map((v) =>
                v.id === vars.versionId
                  ? {
                      ...v,
                      comments: [
                        ...v.comments,
                        { id: tempId, authorType: "CLIENT", authorName: myName, body: vars.body, createdAt: new Date().toISOString(), visibility: "SHARED" as const },
                      ],
                    }
                  : v,
              ),
            }
          : data,
      );
      return { tempId, versionId: vars.versionId };
    },
    onError: (err, _vars, context) => {
      if (context) {
        qc.setQueryData<ConceptVersionsResponse>(conceptVersionsKey(slotId), (data) =>
          data
            ? {
                ...data,
                versions: data.versions.map((v) =>
                  v.id === context.versionId ? { ...v, comments: v.comments.filter((c) => c.id !== context.tempId) } : v,
                ),
              }
            : data,
        );
      }
      reportError("Your comment was not sent")(err);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) }),
  });

  const decideBrief = useMutation({
    mutationKey: [...planWriteKey(planId), "guestBriefDecision"],
    mutationFn: async (vars: { decision: Decision; reason?: string }) =>
      (
        await axiosInstance.post(`${base}/slots/${slotId}/brief-decision`, {
          decision: vars.decision,
          reason: vars.reason ?? null,
        })
      ).data,
    onMutate: async (vars) => {
      await Promise.all([qc.cancelQueries({ queryKey: slotBriefKey(slotId) }), cancelPlanFetches(qc, planId)]);
      const previous = qc.getQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(slotBriefKey(slotId));
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(slotBriefKey(slotId), (data) =>
        data?.brief
          ? { ...data, brief: { ...data.brief, status: vars.decision === "APPROVED" ? "ACCEPTED" : "CHANGES_REQUESTED" } }
          : data,
      );
      // One answer per post: a brief that went out with its idea answers the
      // post, so the post moves with it (the server decides both).
      const previousSlot = readSlot(qc, planId, slotId);
      if (previousSlot?.status === "IN_CLIENT_REVIEW") {
        patchSlot(qc, planId, slotId, (slot) => ({
          ...slot,
          status: vars.decision === "APPROVED" ? "CLIENT_APPROVED" : "SIGNED_OFF",
        }));
      }
      return { previous, previousSlot };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(slotBriefKey(slotId), context.previous);
      if (context?.previousSlot) putSlot(qc, planId, context.previousSlot);
      reportError("Your answer was not sent")(err);
    },
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      resync();
    },
  });

  const commentOnBrief = useMutation({
    mutationFn: async (vars: { briefId: string; body: string; briefVersion: number }) =>
      (await axiosInstance.post(`${base}/briefs/${vars.briefId}/comments`, { body: vars.body })).data,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: slotBriefKey(slotId) });
      const tempId = `optimistic-${Date.now()}`;
      qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(slotBriefKey(slotId), (data) =>
        data
          ? {
              ...data,
              comments: [
                ...data.comments,
                { id: tempId, authorType: "CLIENT", authorName: myName, body: vars.body, briefVersion: vars.briefVersion, createdAt: new Date().toISOString() },
              ],
            }
          : data,
      );
      return { tempId };
    },
    onError: (err, _vars, context) => {
      if (context) {
        qc.setQueryData<{ brief: ProductionBrief | null; comments: BriefComment[] }>(slotBriefKey(slotId), (data) =>
          data ? { ...data, comments: data.comments.filter((c) => c.id !== context.tempId) } : data,
        );
      }
      reportError("Your comment was not sent")(err);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) }),
  });

  return { decideConcept, commentOnConcept, decideBrief, commentOnBrief };
}
